import { useState, useRef, useMemo, useEffect, type ReactNode } from "react";
import { OrderRowDate } from "src/components/order-tables/OrderRowDate";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { OrderTypeEnum, PaymentStatusEnum } from "src/enums/order.enum";
import { CustomerOrder } from "src/interfaces/order.interface";
import useOutsideClick from "src/hooks/useOutsideClick";
import {
  getOrderStatusStyle,
  getPaymentStatusStyle,
  ORDER_BADGE_CLASS,
  PAYMENT_BADGE_CLASS,
  BADGE_PADDING_STYLE,
} from "src/constants/orderStatusBadges";
import { orderTableMenuItemClass } from "src/constants/orderDrawerButtons";

export interface ActiveOrdersBatchSelection {
  selectedIds: Set<string>;
  onToggle: (correlationId: string, selected: boolean) => void;
  onSelectAll: (select: boolean, visibleCorrelationIds: string[]) => void;
}

export interface ActiveOrdersTableProps {
  customerOrders: CustomerOrder[];
  onRowClick: (order: CustomerOrder, index: number) => void;
  onPayBill: (order: CustomerOrder) => void;
  onPrint?: (order: CustomerOrder) => void;
  onEdit?: (order: CustomerOrder) => void;
  /** Owner/manager batch selection (checkbox column + header select-all). */
  batchSelection?: ActiveOrdersBatchSelection;
  /** Renders above the scrollable table body (e.g. batch toolbar). */
  toolbar?: ReactNode;
}

const EMPTY_CELL = "\u2013"; // en dash, styled muted in table

function orderTypeLabel(orderType: string | undefined): string {
  if (!orderType) return EMPTY_CELL;
  switch (orderType) {
    case OrderTypeEnum.DINE_IN:
      return "Dine In";
    case OrderTypeEnum.PICK_UP:
      return "Pick-up";
    case OrderTypeEnum.DELIVERY:
      return "Delivery";
    default:
      return orderType.replace(/_/g, " ").toLowerCase().replace(/^./, (c: string) => c.toUpperCase());
  }
}

function shortOrderId(correlationId: string | undefined): string {
  if (!correlationId) return EMPTY_CELL;
  return "#" + correlationId.slice(-5);
}

function phoneLast4(phone: string | null | undefined): string {
  if (!phone || !phone.trim()) return EMPTY_CELL;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : digits || EMPTY_CELL;
}

function tableDisplay(orderType: string | undefined, tableNumber: string | number | null | undefined): string {
  if (orderType !== OrderTypeEnum.DINE_IN) return EMPTY_CELL;
  if (tableNumber == null || tableNumber === "" || tableNumber === 0) return "N/A";
  return String(tableNumber);
}

const TABLE_MIN_WIDTH = 1104;

/** Matches Settings → Print receipt language checkboxes (`.receipt-lang-check` in index.css). */
const batchCheckboxClass =
  "receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none";
const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] whitespace-nowrap";
const stickyHeaderClass = "sticky top-0 z-10 bg-[#f1f5f9]";

const ActiveOrdersTable = ({
  customerOrders,
  onRowClick,
  onPayBill,
  onPrint,
  onEdit,
  batchSelection,
  toolbar,
}: ActiveOrdersTableProps) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useOutsideClick({ ref: menuRef, handler: () => setOpenMenuId(null) });

  const visibleCorrelationIds = useMemo(
    () => (customerOrders ?? []).map((o) => o.correlationId).filter((id): id is string => Boolean(id)),
    [customerOrders]
  );

  const allVisibleSelected =
    Boolean(batchSelection) &&
    visibleCorrelationIds.length > 0 &&
    visibleCorrelationIds.every((id) => batchSelection!.selectedIds.has(id));

  useEffect(() => {
    if (!batchSelection || !headerCheckboxRef.current) return;
    const someSelected = visibleCorrelationIds.some((id) => batchSelection.selectedIds.has(id));
    headerCheckboxRef.current.indeterminate = someSelected && !allVisibleSelected;
  }, [batchSelection, visibleCorrelationIds, allVisibleSelected]);

  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden">
      {toolbar != null ? <div className="shrink-0">{toolbar}</div> : null}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-left border-collapse table-auto" style={{ minWidth: TABLE_MIN_WIDTH }}>
          <thead>
            <tr>
              {batchSelection && (
                <th className={`pl-4 pr-2 w-[52px] ${stickyHeaderClass} ${headerCellClass}`}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className={batchCheckboxClass}
                    checked={allVisibleSelected}
                    onChange={(e) => batchSelection.onSelectAll(e.target.checked, visibleCorrelationIds)}
                    aria-label="Select all orders on this page"
                  />
                </th>
              )}
              <th className={`${batchSelection ? "pl-2" : "pl-4"} pr-2 w-12 ${stickyHeaderClass} ${headerCellClass}`}>#</th>
              <th className={`px-2 min-w-[7rem] ${stickyHeaderClass} ${headerCellClass}`}>Date</th>
              <th className={`px-2 min-w-[5.5rem] ${stickyHeaderClass} ${headerCellClass}`}>Order ID</th>
              <th className={`px-2 min-w-[96px] ${stickyHeaderClass} ${headerCellClass}`}>Order type</th>
              <th className={`px-2 min-w-[96px] ${stickyHeaderClass} ${headerCellClass}`}>Channel</th>
              <th className={`px-2 min-w-[118px] ${stickyHeaderClass} ${headerCellClass}`}>Order status</th>
              <th className={`px-2 min-w-[128px] ${stickyHeaderClass} ${headerCellClass}`}>Payment status</th>
              <th className={`px-2 w-14 text-right ${stickyHeaderClass} ${headerCellClass}`}>Table</th>
              <th className={`px-2 w-16 text-right ${stickyHeaderClass} ${headerCellClass}`}>Phone</th>
              <th className={`px-2 min-w-[84px] text-right ${stickyHeaderClass} ${headerCellClass}`}>Total</th>
              <th className={`pl-2 pr-4 text-right w-20 ${stickyHeaderClass} ${headerCellClass}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(customerOrders ?? []).map((order, index) => {
              const orderStatus = (order.orderStatus ?? "").toUpperCase();
              const rowTint =
                orderStatus === "VOIDED"
                  ? "bg-red-50/40"
                  : orderStatus === "CLOSED"
                    ? "bg-emerald-50/30"
                    : orderStatus === "READY"
                      ? "bg-sky-50/30"
                      : "";
              return (
              <tr
                key={order.correlationId ?? index}
                className={`border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB] transition-colors group ${rowTint}`}
              >
                {batchSelection && order.correlationId && (
                  <td className="py-2 pl-4 pr-2 align-middle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className={batchCheckboxClass}
                      checked={batchSelection.selectedIds.has(order.correlationId)}
                      onChange={(e) => batchSelection.onToggle(order.correlationId!, e.target.checked)}
                      aria-label={`Select order ${shortOrderId(order.correlationId)}`}
                    />
                  </td>
                )}
                {batchSelection && !order.correlationId && <td className="py-2 pl-4 pr-2" />}
                <td className={`py-2 ${batchSelection ? "pl-2" : "pl-4"} pr-2 align-middle font-medium`}>
                  {order.currentDayOrderCount ?? <span className="text-[#9CA3AF] font-normal">–</span>}
                </td>
                <td className="py-2 px-2 align-middle min-w-[7rem]">
                  <OrderRowDate createdAt={order.createdAt} />
                </td>
                <td className="py-2 px-2 align-middle min-w-[5.5rem] font-mono text-xs text-[#64748b] bg-slate-50/80 whitespace-nowrap">
                  {shortOrderId(order.correlationId) === EMPTY_CELL ? (
                    <span className="text-[#9CA3AF] font-normal bg-transparent">–</span>
                  ) : (
                    shortOrderId(order.correlationId)
                  )}
                </td>
                <td className="py-2 px-2 align-middle">
                  {orderTypeLabel(order.orderType) === EMPTY_CELL ? (
                    <span className="text-[#9CA3AF] font-normal">–</span>
                  ) : (
                    orderTypeLabel(order.orderType)
                  )}
                </td>
                <td className="py-2 px-2 align-middle text-gray-600">
                  {order.channelName?.trim() || <span className="text-[#9CA3AF] font-normal">–</span>}
                </td>
                <td className="py-2 px-2 align-middle">
                  <span
                    className={`${ORDER_BADGE_CLASS} inline-flex items-center justify-center`}
                    style={{
                      ...BADGE_PADDING_STYLE,
                      backgroundColor: getOrderStatusStyle(order.orderStatus).backgroundColor,
                      color: getOrderStatusStyle(order.orderStatus).color,
                    }}
                  >
                    {getOrderStatusStyle(order.orderStatus).label}
                  </span>
                </td>
                <td className="py-2 px-2 align-middle">
                  {(() => {
                    const paymentStyle = getPaymentStatusStyle(order.payment, order.orderStatus);
                    return (
                      <span
                        className={PAYMENT_BADGE_CLASS}
                        style={{
                          ...BADGE_PADDING_STYLE,
                          backgroundColor: paymentStyle.backgroundColor,
                          color: paymentStyle.color,
                        }}
                      >
                        {paymentStyle.label}
                      </span>
                    );
                  })()}
                </td>
                <td className="py-2 px-2 align-middle text-right tabular-nums">
                  {tableDisplay(order.orderType, order.tableNumber) === EMPTY_CELL ? (
                    <span className="text-[#9CA3AF] font-normal">–</span>
                  ) : (
                    tableDisplay(order.orderType, order.tableNumber)
                  )}
                </td>
                <td className="py-2 px-2 align-middle text-right text-gray-600 tabular-nums">
                  {phoneLast4(order.customerPhoneNumber) === EMPTY_CELL ? (
                    <span className="text-[#9CA3AF] font-normal">–</span>
                  ) : (
                    phoneLast4(order.customerPhoneNumber)
                  )}
                </td>
                <td className="py-2 px-2 align-middle text-right font-semibold text-[#0f172a] tabular-nums">
                  {order.totalCents != null ? formatCentsToDollarsWithDollarSign(order.totalCents) : <span className="text-[#9CA3AF] font-normal">–</span>}
                </td>
                <td className="py-2 pl-2 pr-4 align-middle text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="relative flex items-center justify-end" ref={openMenuId === (order.correlationId ?? "") ? menuRef : undefined}>
                    <button
                      type="button"
                      className="p-1.5 rounded text-gray-500 hover:bg-slate-100 hover:text-gray-700 hover:shadow-sm transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId((prev) => (prev === (order.correlationId ?? "") ? null : (order.correlationId ?? "")));
                      }}
                      aria-label="Actions"
                      aria-expanded={openMenuId === (order.correlationId ?? "")}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                        <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 14a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
                      </svg>
                    </button>
                    {openMenuId === (order.correlationId ?? "") && (() => {
                      const ps = order.payment?.paymentStatus
                        ? String(order.payment.paymentStatus).toUpperCase()
                        : "";
                      const paidOrAuthorized =
                        ps === PaymentStatusEnum.CAPTURED || ps === PaymentStatusEnum.AUTHORIZED;
                      const isChannelOrder = !!(order.channelCorrelationId ?? order.channelOrderNumber);
                      const disableEdit = paidOrAuthorized;
                      const disablePay = isChannelOrder || paidOrAuthorized;
                      return (
                        <div className="absolute right-0 top-full mt-1 z-20 min-w-[140px] py-1 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden">
                          <button
                            type="button"
                            className={orderTableMenuItemClass}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(null);
                              onRowClick(order, index);
                            }}
                          >
                            Details
                          </button>
                          {onPrint && (
                            <button
                              type="button"
                              className={orderTableMenuItemClass}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                onPrint(order);
                              }}
                            >
                              Print
                            </button>
                          )}
                          {onEdit && (
                            <button
                              type="button"
                              disabled={disableEdit}
                              className={orderTableMenuItemClass}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (disableEdit) return;
                                setOpenMenuId(null);
                                onEdit(order);
                              }}
                            >
                              Edit
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={disablePay}
                            className={orderTableMenuItemClass}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (disablePay) return;
                              setOpenMenuId(null);
                              onPayBill(order);
                            }}
                          >
                            Pay
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActiveOrdersTable;
